import {SpeedDetails} from "./useSpeedEditorFacade";
import {useEditor} from "../../form/EditorReact";
import {OptionalEditor} from "../../form/editors/OptionalEditor";
import {SelectionKey, SelectionOption} from "../../form/editors/OneOfEditor";
import {ManyOfEditor} from "../../form/editors/ManyOfEditor";
import {MetronicMultiButtonManyOfEditorXxs} from "../../form/metronic/MetronicMultiButtonManyOfEditor";
import React, {useEffect} from "react";
import {SelectionCriteriaVariant} from "../../pages/selection/useFanCriteria";
import {BladeProfileJson} from "../../pages/profile/BladeProfileService";
import {BladeProfileReportJson, BladeProfileReportJsonEntry} from "../../pages/impeller/AdjustableImpellerService";

export type BladeProfileCriteriaContext = {
    availableProfiles: BladeProfileReportJsonEntry[],
    impellerDiameter: number | null,
    speedDetails: SpeedDetails | "invalid-due-to-incorrect-impeller-diameter",
    variant: SelectionCriteriaVariant
}

export function useBladeProfileEditorFacade(props: { context: BladeProfileCriteriaContext }) {

    const availableProfiles = props.context.availableProfiles;

    const bladeProfilesEditor = useEditor<OptionalEditor<SelectionKey[]>>(
        () => new OptionalEditor<SelectionKey[]>(
            new ManyOfEditor(
                availableProfiles.map((availableProfile): SelectionOption => ({
                    key: availableProfile.name,
                    label: availableProfile.displayName,
                })), MetronicMultiButtonManyOfEditorXxs, availableProfiles.map(p => p.name)
            ),
            props.context.variant === SelectionCriteriaVariant.GENERIC_FAN_SELECTION,
            () => <></>,
            (DelegatedEditorComponent) => <DelegatedEditorComponent/>
        ));

    const bladeCountEditor = useEditor<OptionalEditor<SelectionKey[]>>(
        () => new OptionalEditor<SelectionKey[]>(
            new ManyOfEditor(
                [2, 3, 4, 5, 6, 7, 8].map((num): SelectionOption => ({
                    key: num + "",
                    label: num + "",
                })), MetronicMultiButtonManyOfEditorXxs, ["2","3","4","5","6","7","8"]
            ),
            props.context.variant === SelectionCriteriaVariant.GENERIC_FAN_SELECTION,
            () => <></>,
            (DelegatedEditorComponent) => <DelegatedEditorComponent/>
        ));


    useEffect(() => {
        const bannedProfileNames: SelectionKey[] = [];

        availableProfiles.forEach(profile => {
            if (props.context.impellerDiameter != null) {
                if (profile.minDiameterLimit > props.context.impellerDiameter || profile.maxDiameterLimit < props.context.impellerDiameter) {
                    bannedProfileNames.push(profile.name);
                }

                // Speed limiting is considered only if dimaters is known, as profiles are limited by Uz
                if (props.context.speedDetails !== "invalid-due-to-incorrect-impeller-diameter" && props.context.speedDetails !== "speed-autoselection") {
                    let bladeEndSpeed = Math.PI * (props.context.impellerDiameter / 1000) * props.context.speedDetails.rpm / 60.0;
                    if (profile.minBladeEndSpeedLimit > bladeEndSpeed || profile.maxBladeEndSpeedLimit < bladeEndSpeed) {
                        bannedProfileNames.push(profile.name);
                    }
                }
            }
        });

        (bladeProfilesEditor.delegateEditor as ManyOfEditor).banKeys(bannedProfileNames);
    }, [JSON.stringify(props.context)]);

    useEffect(() => {
        // Banned keys on profiles has changed, so we need to adjust available blade counts
        let keysToPotentiallyBan = [2, 3, 4, 5, 6, 7, 8];
        const selectedProfilesKeys = (bladeProfilesEditor.delegateEditor as ManyOfEditor).value;

        props.context.availableProfiles.forEach(profile => {
            if ( selectedProfilesKeys.includes(profile.name) ) {
                profile.statistics.forEach(statOfProfileOptionForSelectedProfile => {
                    keysToPotentiallyBan = keysToPotentiallyBan.filter(key => key != statOfProfileOptionForSelectedProfile.bladeCount);
                    //console.log("FOUND key "+)
                });
            }
        });


        (bladeCountEditor.delegateEditor as ManyOfEditor).banKeys( keysToPotentiallyBan.map(key => key + "") );

    }, [JSON.stringify((bladeProfilesEditor.delegateEditor as ManyOfEditor).value)]);

    return {
        bladeProfilesEditor,
        bladeCountEditor
    }
}